import { useState, useEffect, useCallback } from 'react';
import { Star, Flag, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { adminReviewModerationApi } from '../services/api';
import { VendorReview, VendorReviewListResponse, VendorReviewAdminUpdate } from '../types';
import { decodeHtmlEntities } from '../utils/htmlDecode';

/**
 * AdminReviewModeration Component (Issue #973)
 *
 * Admin interface for moderating vendor reviews
 * Features:
 * - View all reviews (including unpublished and flagged)
 * - Filter by rating, vendor, flagged status, published status
 * - Paginated results
 * - View full review details in modal
 * - Moderate reviews (flag, unpublish, add notes)
 */
export default function AdminReviewModeration() {
  const [reviews, setReviews] = useState<VendorReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [ratingFilter, setRatingFilter] = useState<number | undefined>(undefined);
  const [vendorIdFilter, setVendorIdFilter] = useState<string>('');
  const [isFlaggedFilter, setIsFlaggedFilter] = useState<boolean | undefined>(undefined);
  const [isPublishedFilter, setIsPublishedFilter] = useState<boolean | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>('recent');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [moderateModalOpen, setModerateModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<VendorReview | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Moderation form state
  const [isFlagged, setIsFlagged] = useState(false);
  const [isPublished, setIsPublished] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response: VendorReviewListResponse = await adminReviewModerationApi.getAllReviews(
        page,
        pageSize,
        ratingFilter,
        vendorIdFilter ? parseInt(vendorIdFilter) : undefined,
        isFlaggedFilter,
        isPublishedFilter,
        sortBy
      );
      setReviews(response.reviews);
      setTotal(response.total);
      setTotalPages(response.total_pages);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to load reviews');
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [page, ratingFilter, vendorIdFilter, isFlaggedFilter, isPublishedFilter, sortBy]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleModerateReview = async () => {
    if (!selectedReview) return;
    try {
      setActionLoading(true);
      const updateData: VendorReviewAdminUpdate = {
        is_flagged: isFlagged,
        is_published: isPublished,
        admin_notes: adminNotes || undefined,
      };
      await adminReviewModerationApi.moderateReview(selectedReview.id, updateData);
      setModerateModalOpen(false);
      setSelectedReview(null);
      await loadReviews();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to moderate review');
      console.error('Error moderating review:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const openViewModal = (review: VendorReview) => {
    setSelectedReview(review);
    setViewModalOpen(true);
  };

  const openModerateModal = (review: VendorReview) => {
    setSelectedReview(review);
    setIsFlagged(review.is_flagged);
    setIsPublished(review.is_published);
    setAdminNotes(review.admin_notes || ''); // Issue #973: Pre-fill existing admin notes to prevent accidental overwrites
    setModerateModalOpen(true);
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const resetFilters = () => {
    setRatingFilter(undefined);
    setVendorIdFilter('');
    setIsFlaggedFilter(undefined);
    setIsPublishedFilter(undefined);
    setSortBy('recent');
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="admin-review-moderation">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="review-moderation-title">
            Review Moderation
          </h1>
          <p className="text-gray-600 mt-1">
            Manage and moderate vendor reviews
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert" data-testid="error-message">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6" data-testid="filter-section">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rating
            </label>
            <select
              value={ratingFilter || ''}
              onChange={(e) => {
                setRatingFilter(e.target.value ? parseInt(e.target.value) : undefined);
                setPage(1);
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-dusty-teal focus:ring-dusty-teal"
              data-testid="rating-filter"
            >
              <option value="">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor ID
            </label>
            <input
              type="number"
              value={vendorIdFilter}
              onChange={(e) => {
                setVendorIdFilter(e.target.value);
                setPage(1);
              }}
              placeholder="Filter by vendor"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-dusty-teal focus:ring-dusty-teal"
              data-testid="vendor-filter"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Flagged Status
            </label>
            <select
              value={isFlaggedFilter === undefined ? '' : isFlaggedFilter ? 'true' : 'false'}
              onChange={(e) => {
                setIsFlaggedFilter(e.target.value === '' ? undefined : e.target.value === 'true');
                setPage(1);
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-dusty-teal focus:ring-dusty-teal"
              data-testid="flagged-filter"
            >
              <option value="">All</option>
              <option value="true">Flagged Only</option>
              <option value="false">Not Flagged</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Published Status
            </label>
            <select
              value={isPublishedFilter === undefined ? '' : isPublishedFilter ? 'true' : 'false'}
              onChange={(e) => {
                setIsPublishedFilter(e.target.value === '' ? undefined : e.target.value === 'true');
                setPage(1);
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-dusty-teal focus:ring-dusty-teal"
              data-testid="published-filter"
            >
              <option value="">All</option>
              <option value="true">Published</option>
              <option value="false">Unpublished</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-dusty-teal focus:ring-dusty-teal"
              data-testid="sort-filter"
            >
              <option value="recent">Most Recent</option>
              <option value="helpful">Most Helpful</option>
              <option value="rating_high">Highest Rating</option>
              <option value="rating_low">Lowest Rating</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={resetFilters}
            className="text-sm text-secondary hover:text-ink"
            data-testid="reset-filters"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Total Reviews: <strong>{total}</strong></span>
          <span className="text-gray-600">
            Page {page} of {Math.max(1, totalPages)}
          </span>
        </div>
      </div>

      {/* Reviews Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200" data-testid="reviews-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Review Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Helpful
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Loading reviews...
                  </td>
                </tr>
              ) : reviews.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500" data-testid="no-reviews">
                    No reviews found
                  </td>
                </tr>
              ) : (
                reviews.map((review) => (
                  <tr key={review.id} className="hover:bg-gray-50" data-testid={`review-row-${review.id}`}>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {decodeHtmlEntities(review.customer_display_name || `Customer #${review.customer_id}`)}
                        </div>
                        <div className="text-gray-500">
                          Vendor ID: {review.vendor_id} | Fair: {decodeHtmlEntities(review.fair_name || `Fair #${review.fair_id}`)}
                        </div>
                        {review.review_text && (
                          <div className="text-gray-600 mt-1 line-clamp-2">
                            {decodeHtmlEntities(review.review_text)}
                          </div>
                        )}
                        {review.verified_purchase && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1">
                            <CheckCircle size={12} className="mr-1" />
                            Verified Purchase
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {renderStars(review.rating)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {review.is_flagged && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            <Flag size={12} className="mr-1" />
                            Flagged
                          </span>
                        )}
                        {review.is_published ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            <Eye size={12} className="mr-1" />
                            Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            <EyeOff size={12} className="mr-1" />
                            Unpublished
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {review.helpful_count}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(review.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openViewModal(review)}
                          className="text-secondary hover:text-ink"
                          data-testid={`view-review-${review.id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => openModerateModal(review)}
                          className="text-yellow-600 hover:text-yellow-900"
                          data-testid={`moderate-review-${review.id}`}
                        >
                          <Flag size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center" data-testid="pagination-controls">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="prev-page"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {page} of {Math.max(1, totalPages)}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(Math.max(1, totalPages), p + 1))}
            disabled={page >= Math.max(1, totalPages) || loading}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="next-page"
          >
            Next
          </button>
        </div>
      )}

      {/* View Review Modal */}
      {viewModalOpen && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="view-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Review Details</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reviewer</label>
                  <p className="mt-1">{decodeHtmlEntities(selectedReview.customer_display_name || `Customer #${selectedReview.customer_id}`)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Vendor</label>
                  <p className="mt-1">Vendor ID: {selectedReview.vendor_id}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Fair</label>
                  <p className="mt-1">{decodeHtmlEntities(selectedReview.fair_name || `Fair #${selectedReview.fair_id}`)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Rating</label>
                  <div className="mt-1">{renderStars(selectedReview.rating)}</div>
                </div>

                {selectedReview.review_text && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Review Text</label>
                    <p className="mt-1 text-gray-900 whitespace-pre-wrap">{decodeHtmlEntities(selectedReview.review_text)}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1 flex flex-col gap-1">
                      {selectedReview.is_published ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 w-fit">
                          <Eye size={12} className="mr-1" />
                          Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 w-fit">
                          <EyeOff size={12} className="mr-1" />
                          Unpublished
                        </span>
                      )}
                      {selectedReview.is_flagged && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 w-fit">
                          <Flag size={12} className="mr-1" />
                          Flagged
                        </span>
                      )}
                      {selectedReview.verified_purchase && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 w-fit">
                          <CheckCircle size={12} className="mr-1" />
                          Verified Purchase
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Helpful Votes</label>
                    <p className="mt-1">{selectedReview.helpful_count}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="mt-1">{new Date(selectedReview.created_at).toLocaleString()}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Updated</label>
                  <p className="mt-1">{new Date(selectedReview.updated_at).toLocaleString()}</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setViewModalOpen(false);
                    setSelectedReview(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  data-testid="close-view-modal"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setViewModalOpen(false);
                    openModerateModal(selectedReview);
                  }}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                >
                  Moderate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Moderate Review Modal */}
      {moderateModalOpen && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="moderate-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Moderate Review</h2>

              <div className="mb-4 p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">
                  Review by {decodeHtmlEntities(selectedReview.customer_display_name || `Customer #${selectedReview.customer_id}`)}
                </p>
                <div className="mt-2">{renderStars(selectedReview.rating)}</div>
                {selectedReview.review_text && (
                  <p className="mt-2 text-sm text-gray-900 line-clamp-3">
                    {decodeHtmlEntities(selectedReview.review_text)}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is-flagged"
                    checked={isFlagged}
                    onChange={(e) => setIsFlagged(e.target.checked)}
                    className="rounded border-gray-300 text-secondary focus:ring-dusty-teal"
                    data-testid="flagged-checkbox"
                  />
                  <label htmlFor="is-flagged" className="ml-2 text-sm text-gray-700">
                    Flag this review
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is-published"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    className="rounded border-gray-300 text-secondary focus:ring-dusty-teal"
                    data-testid="published-checkbox"
                  />
                  <label htmlFor="is-published" className="ml-2 text-sm text-gray-700">
                    Publish this review
                  </label>
                </div>

                <div>
                  <label htmlFor="admin-notes" className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes (optional)
                  </label>
                  <textarea
                    id="admin-notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                    maxLength={2000}
                    placeholder="Add notes about this moderation action..."
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-dusty-teal focus:ring-dusty-teal"
                    data-testid="admin-notes"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {adminNotes.length}/2000 characters
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setModerateModalOpen(false);
                    setSelectedReview(null);
                  }}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
                  data-testid="cancel-moderate"
                >
                  Cancel
                </button>
                <button
                  onClick={handleModerateReview}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                  data-testid="submit-moderate"
                >
                  {actionLoading ? 'Saving...' : 'Save Moderation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
